import {
  Box,
  Counter,
  Heading,
  Modal,
  ModalBody,
  ModalHeader,
  SearchInput,
  Text,
  UserIcon,
} from '@razorpay/blade/components';

import React, { useState, useCallback, useEffect } from 'react';
import { EmployeesTable } from './EmployeeTable';
import { useQuery } from 'react-query';
import api from 'api';
import { GetSalaryStructureResponse } from 'components/SalaryStructure/types';
import { salaryStructureQueries } from 'components/SalaryStructures/constants';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { getUserIdWithoutPrefix } from 'utils/commons';
import { SalaryStructuresErrorStateView } from './SalaryStructureErrorStateView';

function SalaryStructureEmployeeDetailsModal({
  isOpen,
  onDismiss,
  salaryStructureData,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  salaryStructureData: GetSalaryStructureResponse | undefined;
}) {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const { debouncedValue: debouncedSearchValue } = useDebouncedValue(searchQuery);
  const [userIds, setUserIds] = useState<string[]>([]);
  useEffect(() => {
    if (!!debouncedSearchValue) {
      setCurrentPage(0);
    }
  }, [debouncedSearchValue]);
  const {
    isFetching,
    isLoading,
    error: errorUserIds,
    isError: isErrorUserIds,
    refetch: refetchUserIds,
  } = useQuery({
    queryKey: [salaryStructureData?.id],
    queryFn: async () => {
      const result = await api.salaryStructures.queries.getUserIdsBySalaryStructure({
        input: {
          salaryStructureId: salaryStructureData?.id || '',
        },
      });
      const userIds = result.payrollGetUserIdsBySalaryStructure.data?.userIds ?? [];
      setUserIds(getUserIdWithoutPrefix(userIds));
    },
    enabled: !!salaryStructureData?.id && userIds.length === 0,
  });

  const {
    data,
    isFetching: isFetchingEmployees,
    isLoading: isLoadingEmployees,
    isError,
    refetch,
    error,
  } = useQuery({
    queryKey: [
      userIds.join(','),
      currentPage,
      pageSize,
      debouncedSearchValue,
      salaryStructureQueries.getUsersByPeopleIds,
    ],
    queryFn: async () => {
      const result = await api.userQueries.getUserByPeopleIds({
        input: {
          userIds: userIds,
          page: currentPage + 1,
          pageSize: pageSize,
          searchQuery: debouncedSearchValue,
        },
      });

      return result.payrollGetUsersByPeopleIds.data;
    },
    enabled: !isFetching && userIds.length > 0,
  });

  const employees = data?.employees ?? [];
  const totalCount = data?.total ?? 0;

  const onPageChange = useCallback(({ page }: { page: number }) => {
    if (page < 0) {
      return;
    }
    setCurrentPage(page);
  }, []);

  const onPageSizeChange = useCallback(({ pageSize }: { pageSize: number }) => {
    setPageSize(pageSize);
    setCurrentPage(0);
  }, []);

  const hasError = isErrorUserIds || isError;

  const refetchFn = () => {
    if (isErrorUserIds || errorUserIds) {
      refetchUserIds();
    } else {
      refetch();
    }
  };
  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} size="large">
      <ModalHeader />
      <ModalBody>
        {hasError ? (
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            height={'570px'}>
            <SalaryStructuresErrorStateView
              title=""
              description="There was an error is fetching the component details."
              retryText="Retry"
              onRetry={refetchFn}
            />
          </Box>
        ) : (
          <>
            <Box display="flex" padding="spacing.7" gap="spacing.4" alignItems={'center'}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                padding="spacing.4"
                width="32px"
                height="32px"
                borderRadius="max"
                backgroundColor="surface.background.sea.subtle">
                <UserIcon color="surface.icon.onSea.onSubtle" size="medium" />
              </Box>
              <Box display="flex" flexDirection="column" gap="spacing.3">
                <Box display="flex" gap="spacing.2">
                  <Heading size="medium" weight="semibold">
                    Employee details
                  </Heading>
                  <Counter value={userIds?.length || 0} color="information" />
                </Box>
                <Text weight="regular" size="small" color="surface.text.gray.muted">
                  Employees on Salary Structure{' '}
                  <Text as="span" weight="regular" size="small" color="surface.text.gray.normal">
                    {salaryStructureData?.name}
                  </Text>
                </Text>
              </Box>
            </Box>

            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.value || '')}
              placeholder="Search Employee"
              accessibilityLabel="Search Employee"
              size="medium"
              onClearButtonClick={() => {
                setSearchQuery('');
                setPageSize(10);
                setCurrentPage(0);
              }}
            />

            <Box marginTop="spacing.7" height={'410px'}>
              <EmployeesTable
                employees={employees || []}
                currentPage={currentPage}
                onPageSizeChange={onPageSizeChange}
                pageSize={pageSize}
                onPageChange={onPageChange}
                isFetching={isFetching || isFetchingEmployees}
                isLoading={isLoadingEmployees || isLoading}
                totalCount={totalCount}
              />
            </Box>
          </>
        )}
      </ModalBody>
    </Modal>
  );
}

export { SalaryStructureEmployeeDetailsModal };
